import React from 'react';
import {get} from 'lodash';
import className from 'classnames';
import moment from 'moment';
import axios from 'axios';

import {
  requestCallback,
} from '../lib/helpers';

import {
  canEdit,
  canKenta,
} from '../lib/policies';

import {
  useCheckIds,
  useModal,
} from '../hooks';

import ImageLoader from './ImageLoader';
import OverflowH from './OverflowH';
import DotTooltip from './DotTooltip';
import PenaltyTools from './PenaltyTools';
import BulkPenaltyTools from './BulkPenaltyTools';
import TableHeader from './TableHeader';
import ChangeBioModal from './ChangeBioModal'

const UsersList = ({
  users = [],
  total = 0,
  withBlockedByCount = false,
  isRecommendListView = false,
}) => {
  const {
    checkIds,
    renderCheckBox, renderCheckBoxForCheckAll,
    renderOpenAllButton,
  } = useCheckIds({
    data: users,
    openUrl: 'users',
  })

  const [
    modal,
    modalSet,
  ] = useModal({
    keyboardBindings: true,
  })

  const onToggleModal = () => {
    modalSet(!modal)
  }

  const bulkForcePhoneVerify = () => {
    console.log('please change api path')
    requestCallback(axios.get(`/${gon.currentProvider}/api/bulk/users/require_phone_verification`, {
      params: {
        ids: checkIds,
      }
    }))
  }

  const bulkForceVerify = () => {
    requestCallback(axios.get(`/${gon.currentProvider}/api/bulk/users/require_id_check`, {
      params: {
        ids: checkIds,
      }
    }))
  }

  const bulkRecommend = () => {
    requestCallback(axios.get(`/${gon.currentProvider}/api/bulk/users/make_recommend`, {
      params: {
        ids: checkIds,
      }
    }))
  }

  return (
    <React.Fragment>
      <div className='MainHeader'>
        <div className='MainHeader__left'>
          <h2 className='MainHeader__h2'>Users list</h2>
          <p className='MainHeader__left__p'>Total {total.toLocaleString()} users</p>
        </div>

        {renderOpenAllButton()}
      </div>

      <div className='TableChecker'>
        {renderCheckBoxForCheckAll()}

        <div className='TableChecker__right'>
          {canEdit('User') &&
            <a
              onClick={bulkForcePhoneVerify}
              disabled={checkIds.length == 0}
              className='TableChecker__right__a TableChecker__right__a--phone-verify'
              title='Phone verify'
            />
          }

          {canEdit('User') &&
            <a
              onClick={bulkForceVerify}
              disabled={checkIds.length == 0}
              className='TableChecker__right__a TableChecker__right__a--flag'
              title='Force verify'
            />
          }

          {canEdit('User') && !isRecommendListView &&
            <a
              onClick={bulkRecommend}
              disabled={checkIds.length == 0}
              className='TableChecker__right__a'
              title='Recommend users'
            />
          }

          {canEdit('User') &&
            <a
              onClick={onToggleModal}
              disabled={checkIds.length == 0}
              className='TableChecker__right__a TableChecker__right__a--pencil'
              title='Change bio'
            />
          }

          {canKenta('User') &&
            <BulkPenaltyTools
              userIds={checkIds}
            />
          }
        </div>
      </div>

      <div
        className={className('Table', {
          'Table--users-list': true,
          'Table--users-list--plus-1': withBlockedByCount,
        })}
      >
        {(() => {
          let columnHeaders = [
            {
              label: '',
            },
            {
              label: 'ID',
              sortKey: 'user_id',
            },
            {
              label: 'Nickname',
            },
            {
              label: 'Bio',
            },
            {
              label: 'Last login',
              sortKey: 'last_loggedin_at',
            },
            {
              label: 'Photo',
              sortKey: 'profile_icon'
            },
            {
              label: 'Status',
              sortKey: 'ban_until'
            },
            {
              label: 'UUID',
            },
            {
              label: 'Reported<br />count',
              sortKey: 'reported_count'
            },
          ]

          if (withBlockedByCount) {
            columnHeaders = [
              ...columnHeaders,
              {
                label: 'Blocked by<br />count',
              },
            ]
          }

          columnHeaders = [
            ...columnHeaders,
            {
              label: 'Manage',
            },
          ]

          return (
            <div className='Table__row'>
              {columnHeaders.map((header, key) => (
                <TableHeader
                  key={key}
                  header={header}
                />
              ))}
            </div>
          )
        })()}

        {users.map((user) => (
          <div
            className={className('Table__row', {
              'Table__row--active': checkIds.includes(user.id),
            })}
            key={user.id}
            data-x={user}
          >
            <div className='Table__col'>
              {renderCheckBox(user)}
            </div>

            <div className='Table__col'>
              <div
                className={className('Table__col__new', {
                  'Table__col__new--active': moment(user.createdAt).isSame(new Date(), 'isoWeek'),
                })}
              >
                <DotTooltip
                  user={user}
                />
              </div>
            </div>

            <div className='Table__col'>{user.nickname}</div>
            <div className='Table__col'>
              <OverflowH
                text={user.biography}
              />
            </div>

            <div
              className='Table__col'
              dangerouslySetInnerHTML={
                {
                  __html: user.lastLoggedinAt !== null ?
                    moment(user.lastLoggedinAt).format('YYYY-MM-DD<br />HH:mm')
                  :
                    'N/A'
                }
              }
            />

            <div className='Table__col'>
              <div className='ImageBorder'>
                <ImageLoader
                  url={get(user, 'profileIcon.thumb.url')}
                />
              </div>
            </div>

            <div className='Table__col'>
              {user.isBanned ?
                <span className='Badge Badge--orange'>Temp banned</span>
              :
                <span className='Badge'>Active</span>
              }
            </div>

            <div className='Table__col'>{user.uuid}</div>

            <div className='Table__col'>{get(user, 'reportedCount', 0)}</div>

            {withBlockedByCount &&
              <div className='Table__col'>{get(user, 'blockedByCount', 0)}</div>
            }

            <div className='Table__col'>
              <div className='Table__controls'>
                {canEdit('User') &&
                  <a
                    className='Table__controls__a Table__controls__a--pencil'
                    href={`/${gon.currentProvider}/users/${user.id}/edit`}
                  />
                }

                {canKenta('User') &&
                  <PenaltyTools
                    tableView
                    userId={user.id}
                    bannedTimeLeft={user.bannedTimeLeft}
                    type="User"
                    banUntil={user.banUntil}
                    isBanned={user.isBanned}
                    uuid={user.uuid}
                  />
                }
              </div>
            </div>
          </div>
        ))}
      </div>

      <ChangeBioModal
        isActive={modal}
        onToggleModal={onToggleModal}
        checkIds={checkIds}
      />
    </React.Fragment>
  );
}

export default UsersList;
